import {
  CLEAN,
  INIT_PLAYER,
  PAUSE,
  PLAY,
  PLAYER_READY,
  SET_VOLUME,
  TOGGLE_MUTE,
  error,
  muted,
  paused,
  playerReady,
  started,
  unmuted,
  volumeSet,
} from '../store/modules/brightcoveRadio'
import {
  __,
  complement,
  compose,
  divide,
  isNil,
  prop,
} from 'ramda'
import { combineEpics, ofType } from 'redux-observable'
import {
  filter,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs'
import { logObservableError, logObservableErrorAndTriggerAction } from '../utilities/logs'
import { resolvePlayerIdentifier } from '../utilities/media/videos'

// initRadioPlayerEpic :: Epic -> Observable Action PLAYER_READY ERROR
export const initRadioPlayerEpic = (action$, state$, { document, getBrightcoveSdk, logger }) =>
  action$.pipe(
    ofType(INIT_PLAYER),
    map(() => document.querySelector('[data-is="live-radio"] video')),
    filter(complement(isNil)),
    mergeMap(video => new Promise(resolve => {
      const playerIdentifier = resolvePlayerIdentifier(video)
      const brightcoveSdk = getBrightcoveSdk(playerIdentifier)
      const player = brightcoveSdk(video)
      player.ready(() => resolve(player))
    })),
    map(playerReady),
    logObservableErrorAndTriggerAction(
      'brightcoveRadio :: initRadioPlayerEpic',
      error,
      logger,
    ),
  )

// playVideoEpic :: Epic -> Observable Action STARTED ERROR
export const playVideoEpic = (action$, state$, { logger }) =>
  action$.pipe(
    ofType(PLAYER_READY),
    switchMap(({ player }) => action$.pipe(
      takeUntil(action$.pipe(ofType(CLEAN))),
      ofType(PLAY),
      mergeMap(() => player.play()),
      map(started),
    )),
    logObservableErrorAndTriggerAction(
      'brightcoveRadio :: playVideoEpic',
      error,
      logger,
    ),
  )

// pauseVideoEpic :: Epic -> Observable Action PAUSED
export const pauseVideoEpic = (action$, state$, { logger }) =>
  action$.pipe(
    ofType(PLAYER_READY),
    switchMap(({ player }) => action$.pipe(
      takeUntil(action$.pipe(ofType(CLEAN))),
      ofType(PAUSE),
      tap(() => player.pause()),
      map(paused),
    )),
    logObservableError('brightcoveRadio :: pauseVideoEpic', logger),
  )

// setVolumeEpic :: Epic -> Observable Action VOLUME_SET
export const setVolumeEpic = (action$, state$, { logger }) =>
  action$.pipe(
    ofType(PLAYER_READY),
    switchMap(({ player }) => action$.pipe(
      takeUntil(action$.pipe(ofType(CLEAN))),
      ofType(SET_VOLUME),
      map(compose(divide(__, 100), prop('level'))),
      tap(vol => player.volume(vol)),
      map(volumeSet),
    )),
    logObservableError('brightcoveRadio :: setVolumeEpic', logger),
  )

// muteSoundEpic :: Epic -> Observable Action MUTED UNMUTED
export const muteSoundEpic = (action$, state$, { logger }) =>
  action$.pipe(
    ofType(PLAYER_READY),
    switchMap(({ player }) => action$.pipe(
      takeUntil(action$.pipe(ofType(CLEAN))),
      ofType(TOGGLE_MUTE),
      tap(({ shouldMute }) => player.muted(shouldMute)),
      map(({ shouldMute }) => shouldMute ? muted() : unmuted()),
    )),
    logObservableError('brightcoveRadio :: muteSoundEpic', logger),
  )

export default combineEpics(
  initRadioPlayerEpic,
  muteSoundEpic,
  pauseVideoEpic,
  playVideoEpic,
  setVolumeEpic,
)
